/**
 * Окно потока разговоров из адреса экрана: период с опорной датой, проекты,
 * категория. Общее для «Возражений» (#846) и «Голоса клиента» (#848): оба
 * экрана держат фильтр в одних и тех же параметрах адреса, поэтому переход
 * между вкладками раздела не сбрасывает период.
 */
import { useCallback, useMemo, useState } from 'react';
import type { Period } from '@/components/layout/PeriodBar';
import { resolveLocale } from '@/lib/utils';
import { usePeriodNavigation, type PeriodDateRange } from '@/hooks/usePeriodNavigation';
import {
  DAY_MS,
  dayParts,
  type ObjectionsSearch,
} from '@/components/analytics/objections/objections-search';

export type StreamSearch = Pick<
  ObjectionsSearch,
  'period' | 'anchor' | 'projectIds' | 'category' | 'demo'
> & {
  /**
   * An exact window (ISO instants) from a link — the monthly Voice of
   * Customer email opens the screen on the org's month, which the viewer's
   * timezone would draw differently. Wins over the period while present; any
   * step or period change drops it and returns to ordinary periods.
   */
  from?: string;
  to?: string;
  /**
   * The org's calendar day ('YYYY-MM-DD') of that window — what the period bar
   * names, instead of the viewer's date of the window's midpoint (an org at
   * UTC+13 has its 1 October start on 30 September in UTC). Drops with it.
   */
  day?: string;
};

/** «5 Oct 2026 – 11 Oct 2026», or one day when the window is a day. */
export function exactWindowLabel(from: string, to: string): string {
  const day = new Intl.DateTimeFormat(resolveLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const first = day.format(new Date(from));
  const last = day.format(new Date(to));
  return first === last ? first : `${first} – ${last}`;
}

/** Both bounds fall on the viewer's midnights: `[from, to + 1 ms)` is whole local days. */
function isLocalDayWindow(from: string, to: string): boolean {
  const atMidnight = (date: Date) =>
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0;
  return atMidnight(new Date(from)) && atMidnight(new Date(Date.parse(to) + 1));
}

/**
 * The last calendar day ('YYYY-MM-DD') of an exact window whose first day is
 * `day` — in the window's own zone, by its length in whole days (a clock
 * change inside it is an hour, not a day). `undefined` — no such day.
 */
export function windowLastDay(day: string | undefined, from: string, to: string) {
  const parts = dayParts(day);
  if (!parts) return undefined;
  const days = Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS));
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + days - 1))
    .toISOString()
    .slice(0, 10);
}

export const ALL_CATEGORIES = 'all';
const NO_PROJECTS: string[] = [];

export function useStreamWindow(
  search: StreamSearch,
  onSearchChange: (patch: Partial<StreamSearch>) => void
) {
  const period: Period = search.period ?? 'month';
  // The anchor lives in the address only; without one the screen shows the
  // period around the moment it was opened.
  const [openedAt] = useState(() => new Date());
  const exactDay = search.from && search.to ? search.day : undefined;
  const anchor = useMemo(() => {
    // The org's day wins over the link's instant: noon of that date in the
    // viewer's own zone, so the bar names it and a step goes from it.
    const day = dayParts(exactDay);
    if (day) return new Date(day[0], day[1] - 1, day[2], 12);
    return search.anchor ? new Date(search.anchor) : openedAt;
  }, [exactDay, search.anchor, openedAt]);
  const onAnchorChange = useCallback(
    (date: Date) =>
      onSearchChange({
        anchor: date.toISOString(),
        from: undefined,
        to: undefined,
        day: undefined,
        // The card's demo rule goes with its window: this screen has no control
        // for it, so a step would otherwise pin it for good.
        demo: undefined,
      }),
    [onSearchChange]
  );
  const anchorControl = useMemo(
    () => ({ anchor, onAnchorChange }),
    [anchor, onAnchorChange]
  );
  const { dateRange: periodRange, navigation: periodNavigation } = usePeriodNavigation(
    period,
    anchorControl
  );
  const dateRange = useMemo<PeriodDateRange & { lastDay?: string }>(
    () =>
      search.from && search.to
        ? {
            ...periodRange,
            start: new Date(search.from),
            end: new Date(search.to),
            periodStart: search.from,
            periodEnd: search.to,
            // The organization's last day: the series' year ends with its
            // month, whatever the viewer's zone makes of the window's end.
            lastDay: windowLastDay(exactDay, search.from, search.to),
          }
        : periodRange,
    [periodRange, search.from, search.to, exactDay]
  );
  // An exact window of the viewer's own days that is not the period's (a
  // dashboard card's seven days under the default month tab) is named by those
  // days: the bar must not call the queried range a month. An email's window
  // is the organization's days, which the viewer's zone would draw wrongly —
  // that one is named by its `day` through the anchor.
  const navigation = useMemo(() => {
    if (!search.from || !search.to) return periodNavigation;
    if (
      !isLocalDayWindow(search.from, search.to) ||
      (search.from === periodRange.periodStart && search.to === periodRange.periodEnd)
    ) {
      return periodNavigation;
    }
    return {
      ...periodNavigation,
      label: exactWindowLabel(search.from, search.to),
      exactWindow: true,
    };
  }, [periodNavigation, periodRange, search.from, search.to]);
  return {
    period,
    dateRange,
    navigation,
    projectIds: search.projectIds ?? NO_PROJECTS,
    callCategory: search.category ?? ALL_CATEGORIES,
  };
}
